import PropTypes from "prop-types";
import React, { Component } from "react";
import classNames from "classnames";
import theme from "styles/theme";
import { Form } from "formsy-react";
import IconMenu from "material-ui/IconMenu";
import IconButton from "material-ui/IconButton";
import MoreVert from "material-ui/svg-icons/navigation/more-vert";
import FilterIcon from "components/icons/refine";
import Dialog from "components/dialog";
import MenuItem from "material-ui/MenuItem";
import { VIEWPORT_MOBILE, VIEWPORT_PHABLET } from "constants/media";

/** @augments {Component<any, any>} */
class ToolbarGroup extends Component {
    static contextTypes = {
        viewport: PropTypes.string.isRequired,
        sidebarSections: PropTypes.array,
        filters: PropTypes.object,
    };

    static propTypes = {
        children: PropTypes.node,
        style: PropTypes.object,
        disabled: PropTypes.bool,
        convertButtons: PropTypes.bool,
        useOnlySearch: PropTypes.bool,
        className: PropTypes.string,
    };

    static defaultProps = {
        disabled: false,
    };

    constructor(props) {
        super(props);
        this.state = {
            filterDialogOpen: false,
            iconMenuOpen: false,
        };

        this.handleFiltersOpen = this.handleFiltersOpen.bind(this);
        this.handleFiltersClose = this.handleFiltersClose.bind(this);
    }

    componentWillUnmount() {
        this.unmounted = true;
    }

    handleFiltersOpen() {
        this.setState({ filterDialogOpen: true });
    }

    handleFiltersClose() {
        this.setState({ filterDialogOpen: false });
    }

    handleRequestChange = (open) => {
        setTimeout(() => {
            if (!this.unmounted) {
                this.setState({ iconMenuOpen: open });
            }
        }, 50);
    };

    calculateTopMargin(componentHeight) {
        return (60 - componentHeight) / 2;
    }

    render() {
        const { viewport, sidebarSections } = this.context;
        const { children, disabled, convertButtons, useOnlySearch, className, ...other } = this.props;

        const { filterDialogOpen } = this.state;

        const isMobile = viewport === VIEWPORT_PHABLET || viewport === VIEWPORT_MOBILE;
        let newChildren = [];
        const pullouts = [];
        let search = null;

        const getNode = (currentChild, top, styles = {}, disabled = false) =>
            React.cloneElement(currentChild, {
                style: currentChild.props["data-own-align"]
                    ? currentChild.props.style
                    : Object.assign({}, currentChild.props.style, {
                          marginTop: this.calculateTopMargin(top) + "px",
                          marginLeft: "24px",
                          verticalAlign: "top",
                          ...styles,
                      }),
                disabled: disabled || currentChild.props.disabled,
            });

        React.Children.forEach(
            children,
            (currentChild, i) => {
                if (!currentChild) {
                    return;
                }

                if (!currentChild.type) {
                    newChildren.push(currentChild);
                    return;
                }

                if (isMobile && currentChild.key === "search") {
                    search = React.cloneElement(currentChild, {
                        style: Object.assign({}, currentChild.props.style, {
                            marginTop: "16px",
                            paddingRight: "10px",
                        }),
                    });
                    return;
                }

                const childType =
                    currentChild.type.muiName ||
                    currentChild.displayName ||
                    currentChild.type.displayName ||
                    currentChild.type.name;

                switch (childType) {
                    case "Constructor": // Formsy Decorator is bad
                    case "Select":
                        newChildren.push(getNode(currentChild, 36));
                        break;
                    case "IconButton":
                    case "Button":
                        if (isMobile && convertButtons) {
                            newChildren.push(
                                <MenuItem
                                    key={currentChild.key}
                                    primaryText={currentChild.props.label || currentChild.props.tooltip}
                                    onClick={this.onMenuClick(currentChild.props.onClick)}
                                    disabled={disabled || currentChild.props.disabled}
                                />
                            );
                            break;
                        } else {
                            newChildren.push(
                                <div key={currentChild.key} onClick={this.handleRequestChange.bind(this, false)}>
                                    {getNode(currentChild, 40, {}, disabled)}
                                </div>
                            );
                        }
                        break;
                    case "Field":
                        newChildren.push(
                            <div key={currentChild.key} onClick={this.handleRequestChange.bind(this, false)}>
                                {getNode(currentChild, 40, {}, disabled)}
                            </div>
                        );
                        break;
                    case "IconMenu":
                        if (isMobile) {
                            if (currentChild.props["data-pullout"]) {
                                pullouts.push(getNode(currentChild, 48, { marginLeft: "0px" }));
                            } else {
                                React.Children.map(currentChild.props.children, (subchild) => {
                                    newChildren.push(subchild);
                                });
                            }
                        } else {
                            if (currentChild.props.iconButtonElement) {
                                const subchildType =
                                    currentChild.props.iconButtonElement.type.muiName ||
                                    currentChild.props.iconButtonElement.displayName ||
                                    currentChild.props.iconButtonElement.type.name;
                                if (subchildType === "Button") {
                                    newChildren.push(getNode(currentChild, 40));
                                    return;
                                }
                            }
                            let offset = 0;
                            if (i === children.length - 1) {
                                offset = -16;
                            }
                            newChildren.push(
                                getNode(currentChild, 48, { marginLeft: "0", marginRight: `${offset}px` })
                            );
                        }
                        break;
                    default:
                        if (isMobile && currentChild.props["data-pullout"]) {
                            pullouts.push(getNode(currentChild, 40, { marginLeft: "0px" }));
                        } else {
                            newChildren.push(getNode(currentChild, 40));
                        }
                }
            },
            this
        );

        if (isMobile) {
            if (newChildren.length) {
                newChildren = [
                    ...pullouts,
                    <IconMenu
                        key="more"
                        iconButtonElement={
                            <IconButton>
                                <MoreVert color={theme.palette.darkGrey} hoverColor={theme.palette.darkerGrey} />
                            </IconButton>
                        }
                        anchorOrigin={{ horizontal: "right", vertical: "top" }}
                        targetOrigin={{ horizontal: "right", vertical: "top" }}
                        style={{
                            marginTop: this.calculateTopMargin(48) + "px",
                            verticalAlign: "top",
                        }}
                        open={this.state.iconMenuOpen}
                        onRequestChange={this.handleRequestChange}
                    >
                        {newChildren}
                    </IconMenu>,
                ];
            } else {
                newChildren = pullouts;
            }

            let filters = null;
            if (sidebarSections) {
                sidebarSections.forEach((section) => {
                    if (section.key === "filters") {
                        filters = section;
                    }
                });
            }

            if (filters || (useOnlySearch && search)) {
                newChildren.unshift(
                    <IconButton
                        key="filter-list"
                        style={{
                            marginTop: this.calculateTopMargin(48) + "px",
                            verticalAlign: "top",
                        }}
                        onClick={this.handleFiltersOpen}
                    >
                        <FilterIcon
                            color={
                                filters &&
                                filters.props &&
                                filters.props.activeFiltersValues &&
                                filters.props.activeFiltersValues.size
                                    ? theme.palette.primary1Color
                                    : theme.palette.darkGrey
                            }
                            hoverColor={
                                filters &&
                                filters.props &&
                                filters.props.activeFiltersValues &&
                                filters.props.activeFiltersValues.size
                                    ? theme.palette.primary1Color
                                    : theme.palette.darkerGrey
                            }
                        />
                    </IconButton>,
                    <Dialog
                        key="filters-dialog"
                        open={filterDialogOpen}
                        onRequestClose={this.handleFiltersClose}
                        autoScrollBodyContent={true}
                    >
                        {search}
                        {filters}
                    </Dialog>
                );
            }
        }

        return (
            <Form
                className={classNames("action-form", className)}
                style={{
                    display: "flex",
                    paddingRight: isMobile ? "0px" : "16px",
                    height: "60px",
                    zIndex: 100,
                }}
                {...other}
            >
                {newChildren}
            </Form>
        );
    }

    onMenuClick = (onClick) => () => {
        onClick();
        this.handleRequestChange(false);
    };
}

export default ToolbarGroup;
